// 슬롯별 사진 선택 · 직접 업로드 · 9:16 크롭.
//
// 자동 선별이 매장 외관과 잡동사니를 구분하지 못해 확정을 사람에게 넘겼다(2026-08-27).
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Check, ImagePlus, LoaderCircle, Move, RefreshCw, ZoomIn } from "lucide-react";

const ASPECT = 3 / 4;               // 출력 카드 1440×1920 = 3:4

export interface Candidate {
  id: string; venueIndex: number; slot: "store" | "food" | "cover";
  rank: number; origin: "collected" | "uploaded"; url: string;
  width: number; height: number; bytes: number; sourceCredit: string | null;
}
export interface Selection {
  venueIndex: number; slot: string; candidateId: string;
  crop: { x: number; y: number; w: number; h: number }; selectedAt: string;
}

// 처음 고른 사진의 기본 크롭. {0,0,1,1}(사진 전체)로 두면 4:3 사진이 3:4 칸에 눌린다.
const defaultCrop = (c: { width: number; height: number }) =>
  cropFromView(c.width || 1000, c.height || 1000, 1, 0.5, 0.5);

const SLOT_LABEL: Record<string, string> = { venue: "이 가게에 쓸 사진", cover: "표지 배경", store: "매장 사진", food: "음식 사진" };

// 확대 배율 → 정규 크롭 사각형. zoom 1 이면 9:16 을 최대로 잡는다.
function cropFromView(imgW: number, imgH: number, zoom: number, cx: number, cy: number) {
  if (!imgW || !imgH) return { x: 0, y: 0, w: 1, h: 1 };
  const imgAspect = imgW / imgH;
  let w: number; let h: number;
  if (imgAspect > ASPECT) { h = 1 / zoom; w = (h * imgH * ASPECT) / imgW; }
  else { w = 1 / zoom; h = (w * imgW) / (ASPECT * imgH); }
  w = Math.min(1, w); h = Math.min(1, h);
  const x = Math.min(1 - w, Math.max(0, cx - w / 2));
  const y = Math.min(1 - h, Math.max(0, cy - h / 2));
  return { x, y, w, h };
}

function CropEditor({ candidate, crop, onCommit, saving }: {
  candidate: Candidate;
  crop: { x: number; y: number; w: number; h: number };
  onCommit: (crop: { x: number; y: number; w: number; h: number }) => void;
  saving: boolean;
}) {
  const imgW = candidate.width || 1000;
  const imgH = candidate.height || 1000;
  const base = useMemo(() => cropFromView(imgW, imgH, 1, 0.5, 0.5), [imgW, imgH]);

  // 드래그 중에는 React 상태를 건드리지 않는다 — ref 에만 쓰고 DOM transform 으로 반영한다.
  const view = useRef({
    zoom: base.w > 0 ? Math.max(1, Math.min(4, base.w / (crop.w || base.w))) : 1,
    cx: crop.x + crop.w / 2,
    cy: crop.y + crop.h / 2,
  });
  const [zoomLabel, setZoomLabel] = useState(view.current.zoom);
  const [dirty, setDirty] = useState(false);
  const stageRef = useRef<HTMLDivElement | null>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const drag = useRef<{ px: number; py: number; cx: number; cy: number } | null>(null);
  const frame = useRef(0);

  const paint = useCallback(() => {
    frame.current = 0;
    const stage = stageRef.current;
    const img = imgRef.current;
    if (!stage || !img) return;
    const c = cropFromView(imgW, imgH, view.current.zoom, view.current.cx, view.current.cy);
    const sw = stage.clientWidth;
    const sh = stage.clientHeight;
    // 크롭 사각형이 무대를 정확히 채우도록 이미지를 확대·이동한다.
    const w = sw / c.w;
    const h = sh / c.h;
    img.style.width = `${w}px`;
    img.style.height = `${h}px`;
    img.style.transform = `translate3d(${-c.x * w}px, ${-c.y * h}px, 0)`;
  }, [imgW, imgH]);

  const schedule = useCallback(() => {
    if (frame.current) return;
    frame.current = requestAnimationFrame(paint);
  }, [paint]);

  useEffect(() => {
    view.current = {
      zoom: base.w > 0 ? Math.max(1, Math.min(4, base.w / (crop.w || base.w))) : 1,
      cx: crop.x + crop.w / 2,
      cy: crop.y + crop.h / 2,
    };
    setZoomLabel(view.current.zoom);
    setDirty(false);
    paint();
  }, [candidate.id, crop.x, crop.y, crop.w, crop.h, base.w, paint]);

  useEffect(() => () => { if (frame.current) cancelAnimationFrame(frame.current); }, []);

  const current = () => cropFromView(imgW, imgH, view.current.zoom, view.current.cx, view.current.cy);

  return (
    <div className="fp-crop">
      <div
        className="fp-crop-stage"
        ref={stageRef}
        onPointerDown={(e) => {
          (e.target as HTMLElement).setPointerCapture(e.pointerId);
          drag.current = { px: e.clientX, py: e.clientY, cx: view.current.cx, cy: view.current.cy };
        }}
        onPointerMove={(e) => {
          const d = drag.current;
          const stage = stageRef.current;
          if (!d || !stage) return;
          const c = current();
          view.current.cx = d.cx - ((e.clientX - d.px) / stage.clientWidth) * c.w;
          view.current.cy = d.cy - ((e.clientY - d.py) / stage.clientHeight) * c.h;
          setDirty(true);
          schedule();
        }}
        onPointerUp={() => { drag.current = null; }}
        onPointerCancel={() => { drag.current = null; }}
        onWheel={(e) => {
          e.preventDefault();
          view.current.zoom = Math.max(1, Math.min(4, view.current.zoom * (e.deltaY < 0 ? 1.08 : 0.93)));
          setZoomLabel(view.current.zoom);
          setDirty(true);
          schedule();
        }}
      >
        <img
          ref={imgRef}
          src={`${candidate.url}?s=m`}
          alt=""
          draggable={false}
          decoding="async"
          onLoad={paint}
        />
      </div>
      <div className="fp-crop-controls">
        <span className="fp-hint"><Move size={13} /> 끌어서 위치</span>
        <label className="fp-zoom">
          <span><ZoomIn size={13} /> {zoomLabel.toFixed(2)}×</span>
          <input
            type="range" min={1} max={4} step={0.02} value={zoomLabel}
            onChange={(e) => {
              view.current.zoom = Number(e.target.value);
              setZoomLabel(view.current.zoom);
              setDirty(true);
              schedule();
            }}
          />
        </label>
        <div className="fp-crop-buttons">
          <button className="primary" disabled={!dirty || saving} onClick={() => { onCommit(current()); setDirty(false); }}>
            {saving ? <LoaderCircle className="spin" size={13} /> : <Check size={13} />} 적용
          </button>
          <button
            className="ghost"
            onClick={() => {
              view.current = { zoom: 1, cx: 0.5, cy: 0.5 };
              setZoomLabel(1); setDirty(true); paint();
            }}
          >초기화</button>
        </div>
      </div>
    </div>
  );
}

export function FoodPhotoPicker({ postId, venues, onRebuilt }: {
  postId: string;
  venues: Array<{ index: number; name: string }>;
  onRebuilt?: (post: unknown) => void;
}) {
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [selections, setSelections] = useState<Selection[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [openSlot, setOpenSlot] = useState<string | null>(null);
  const [rebuilt, setRebuilt] = useState<{ applied: number; skipped: Array<{ position: number; reason: string }> } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`/api/food/${encodeURIComponent(postId)}/photos`, { credentials: "same-origin" });
      const body = await res.json();
      if (!res.ok || !body.ok) throw new Error(`${body.error ?? body.message ?? "후보를 불러오지 못했어요"} (HTTP ${res.status}${body.code ? " · " + body.code : ""})`);
      setCandidates(body.candidates ?? []);
      setSelections(body.selections ?? []);
      setError(null);
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
    finally { setLoading(false); }
  }, [postId]);

  useEffect(() => { void load(); }, [load]);

  const selectionFor = (venueIndex: number, slot: string) =>
    selections.find((s) => s.venueIndex === venueIndex && s.slot === slot) ?? null;

  const save = async (venueIndex: number, slot: string, candidateId: string, crop: Selection["crop"]) => {
    const key = `${venueIndex}:${slot}`;
    setBusy(key);
    try {
      const res = await fetch(`/api/food/${encodeURIComponent(postId)}/photo-selection`, {
        method: "POST", credentials: "same-origin",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ venueIndex, slot, candidateId, crop }),
      });
      const body = await res.json();
      if (!res.ok || !body.ok) throw new Error(`${body.error ?? body.message ?? "저장 실패"} (HTTP ${res.status}${body.code ? " · " + body.code : ""})`);
      setCandidates(body.candidates ?? []);
      setSelections(body.selections ?? []);
      setError(null);
      // 고른 즉시 그 칸만 다시 만들어 미리보기에 반영한다. 전체 10장을 굽지 않는다.
      await rebuildOne(venueIndex, slot);
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(null); }
  };

  const rebuildOne = async (venueIndex: number, slot: string) => {
    try {
      const res = await fetch(`/api/food/${encodeURIComponent(postId)}/rebuild`, {
        method: "POST", credentials: "same-origin",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ venueIndex, slot }),
      });
      const body = await res.json();
      if (res.ok && body.ok && body.post) onRebuilt?.(body.post);
    } catch { /* 미리보기 반영 실패는 선택 자체를 되돌릴 이유가 안 된다 */ }
  };

  const upload = async (venueIndex: number, slot: string, file: File) => {
    const key = `up:${venueIndex}:${slot}`;
    setBusy(key);
    try {
      const form = new FormData();
      form.set("file", file);
      form.set("venueIndex", String(venueIndex));
      form.set("slot", slot);
      const res = await fetch(`/api/food/${encodeURIComponent(postId)}/photos`, {
        method: "POST", credentials: "same-origin", body: form,
      });
      const body = await res.json();
      if (!res.ok || !body.ok) throw new Error(`${body.error ?? body.message ?? "업로드 실패"} (HTTP ${res.status}${body.code ? " · " + body.code : ""})`);
      setCandidates(body.candidates ?? []);
      setSelections(body.selections ?? []);
      setError(null);
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(null); }
  };

  // 고른 사진을 실제 슬라이드에 반영한다. 서버가 사진만 갈아 끼우고 같은 오버레이를 다시 얹는다.
  const rebuild = async () => {
    setBusy("rebuild");
    try {
      const res = await fetch(`/api/food/${encodeURIComponent(postId)}/rebuild`, {
        method: "POST", credentials: "same-origin", headers: { "content-type": "application/json" }, body: "{}",
      });
      const body = await res.json();
      if (!res.ok || !body.ok) throw new Error(`${body.error ?? "다시 만들지 못했어요"} (HTTP ${res.status}${body.code ? " · " + body.code : ""})`);
      setRebuilt({ applied: body.applied?.length ?? 0, skipped: body.skipped ?? [] });
      setError(null);
      onRebuilt?.(body.post);
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(null); }
  };

  if (loading) return <div className="fp-loading"><LoaderCircle className="spin" size={18} /> 사진 후보를 불러오는 중</div>;

  const done = selections.length;
  // 표지 1장 + 가게마다 1장 = 슬라이드 수. Instagram 캐러셀 상한 10장.
  const total = venues.length + 1;

  const renderSlot = (venueIndex: number, slot: "cover" | "venue") => {
    const list = candidates.filter((c) => c.venueIndex === venueIndex && c.slot === slot);
    const sel = selectionFor(venueIndex, slot);
    const chosen = list.find((c) => c.id === sel?.candidateId) ?? null;
    const key = `${venueIndex}:${slot}`;
    const open = openSlot === key;
    return (
      <div className="fp-slot" key={slot}>
        <div className="fp-slot-head">
          <span>{SLOT_LABEL[slot]}</span>
          {sel
            ? <span className="fp-ok"><Check size={13} /> 임시저장됨</span>
            : <span className="fp-todo">미선택</span>}
        </div>
        <div className="fp-thumbs">
          {list.map((c) => (
            <button
              key={c.id}
              className={`fp-thumb ${sel?.candidateId === c.id ? "on" : ""}`}
              disabled={busy === key}
              onClick={() => {
                save(venueIndex, slot, c.id, sel?.candidateId === c.id ? sel.crop : defaultCrop(c));
                setOpenSlot(key);
              }}
            >
              <img src={`${c.url}?s=t`} alt="" loading="lazy" decoding="async" width={88} height={117} />
              {c.origin === "uploaded" && <em>내 업로드</em>}
            </button>
          ))}
          {list.length === 0 && <span className="fp-none">후보 없음 — 직접 올려 주세요</span>}
          <label className={`fp-upload ${busy === `up:${key}` ? "busy" : ""}`}>
            {busy === `up:${key}` ? <LoaderCircle className="spin" size={17} /> : <ImagePlus size={17} />}
            <span>직접 올리기</span>
            <input
              type="file" accept="image/jpeg,image/png,image/webp" hidden
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void upload(venueIndex, slot, f);
                e.target.value = "";
              }}
            />
          </label>
        </div>
        {open && chosen && (
          <CropEditor
            candidate={chosen}
            crop={sel?.crop ?? defaultCrop(chosen)}
            saving={busy === key}
            onCommit={(crop) => save(venueIndex, slot, chosen.id, crop)}
          />
        )}
        {chosen && !open && (
          <button className="ghost fp-edit" onClick={() => setOpenSlot(key)}>크롭 조정</button>
        )}
      </div>
    );
  };

  return (
    <div className="fp">
      <div className="fp-head">
        <strong>사진 선택</strong>
        <span className="fp-progress">{done}/{total} 임시저장</span>
        {error && <span className="fp-error">{error}</span>}
      </div>
      <div className="fp-venue">
        <div className="fp-venue-name">표지</div>
        {renderSlot(0, "cover")}
      </div>
      {venues.map((venue) => (
        <div className="fp-venue" key={venue.index}>
          <div className="fp-venue-name">{venue.index + 1}. {venue.name}</div>
          {renderSlot(venue.index, "venue")}
        </div>
      ))}
      <div className="fp-foot">
        <button className="primary" disabled={done === 0 || busy === "rebuild"} onClick={() => void rebuild()}>
          {busy === "rebuild" ? <LoaderCircle className="spin" size={15} /> : <RefreshCw size={15} />}
          고른 사진으로 다시 만들기
        </button>
        <span className="fp-foot-note">
          {done === 0
            ? "사진을 고르면 임시저장되고, 여기서 슬라이드에 반영합니다."
            : `임시저장 ${done}장 · 누르면 슬라이드가 바뀝니다.`}
        </span>
        {rebuilt && (
          <span className={rebuilt.skipped.length ? "fp-todo" : "fp-ok"}>
            {rebuilt.applied}장 반영{rebuilt.skipped.length ? ` · ${rebuilt.skipped.length}장 건너뜀` : ""}
          </span>
        )}
      </div>
    </div>
  );
}
